"use client";

import { useEffect, useRef, useState } from "react";
import { faqKey } from "@/lib/trip/faqs";

type Answers = Record<string, string>;

// One request per package, shared by every lazy answer on the page.
const requests = new Map<number, Promise<Answers>>();

function loadAnswers(packageId: number): Promise<Answers> {
  let req = requests.get(packageId);
  if (!req) {
    req = fetch(`/api/faqs/?package=${packageId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d: { answers: Answers }) => d.answers)
      .catch((err) => {
        requests.delete(packageId); // allow a retry on the next open
        throw err;
      });
    requests.set(packageId, req);
  }
  return req;
}

/**
 * FAQ answer that isn't in the initial HTML: fetched (sanitized, server-side)
 * the first time its enclosing <details> is opened. Keeps long FAQ lists from
 * inflating the page while every question stays server-rendered.
 */
export function LazyFaqAnswer({
  packageId,
  question,
  className,
}: {
  packageId: number;
  question: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (html !== null) return;
    const details = ref.current?.closest("details");
    if (!details) return;
    const onToggle = () => {
      if (!details.open) return;
      setFailed(false);
      loadAnswers(packageId)
        .then((answers) => setHtml(answers[faqKey(question)] ?? ""))
        .catch(() => setFailed(true));
    };
    onToggle();
    details.addEventListener("toggle", onToggle);
    return () => details.removeEventListener("toggle", onToggle);
  }, [packageId, question, html]);

  if (html !== null) {
    return (
      <div
        ref={ref}
        className={className}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return (
    <div ref={ref} className={className} aria-live="polite">
      <p className="text-muted">
        {failed
          ? "Couldn't load this answer — close and reopen to try again."
          : "Loading…"}
      </p>
    </div>
  );
}
